"use client";

import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

interface SignInButtonProps {
  onClick: () => void;
  /** Smaller padding + text for mobile header. */
  compact?: boolean;
}

/**
 * The colourful purple/lime "Sign In" pill with shimmer + sparkles.
 * Single source of truth so the mobile header and the desktop AuthButton
 * always render the same affordance.
 */
export default function SignInButton({ onClick, compact = false }: SignInButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`group relative inline-flex items-center gap-2 bg-[#7C3AED] dark:bg-[#CCFF00] text-white dark:text-[#0A0A0A] rounded-[5px] font-inter-tight font-bold hover:bg-[#6D28D9] dark:hover:bg-[#00FF9D] hover:scale-105 active:scale-95 transition-all duration-200 overflow-hidden ${
        compact
          ? "h-8 px-3 text-[12px]"
          : "px-5 py-1.5 text-sm"
      }`}
    >
      {/* Shimmer sweep — soft highlight travels across the button on
          loop to invite the click without being loud. We animate
          `transform: translateX` instead of `background-position` so the
          highlight runs on the GPU compositor; the previous bg-position
          version was flagged by chrome-devtools as a CLS culprit
          (TARGET_HAS_INVALID_COMPOSITING_STATE) because each frame
          invalidated layout. */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[200%] pointer-events-none"
        style={{
          background:
            "linear-gradient(100deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%)",
          animation: "signin-shimmer 2.4s ease-in-out infinite",
          willChange: "transform",
        }}
      />
      <motion.span
        aria-hidden
        animate={{
          rotate: [0, -12, 12, -8, 0],
          scale: [1, 1.18, 1, 1.1, 1],
        }}
        transition={{
          duration: 2.4,
          repeat: Infinity,
          ease: "easeInOut",
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="relative flex items-center justify-center"
      >
        <Sparkles size={compact ? 12 : 14} strokeWidth={2.4} fill="currentColor" />
      </motion.span>
      <span className="relative">Sign In</span>
      <style jsx>{`
        @keyframes signin-shimmer {
          0% { transform: translateX(-100%); }
          60% { transform: translateX(0%); }
          100% { transform: translateX(0%); }
        }
      `}</style>
    </button>
  );
}
